import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { useBluelagoon } from "@bluelagoon";
import { Loader2, Shield, RotateCcw, AlertCircle, CheckCircle } from "lucide-react";

export default function OverridePanel({ companyId, companyName, currentOverride, onUpdated }) {
    const [limit, setLimit] = useState(currentOverride || "");
    const [confirmAction, setConfirmAction] = useState(null);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const { call: setOverride, isLoading: settingOverride } = useBluelagoon({ method: "setAutomationOverride" });
    const { call: resetUsage, isLoading: resetting } = useBluelagoon({ method: "resetAutomationUsageAdmin" });

    const handleSetOverride = async () => {
        setConfirmAction(null);
        setSuccess(null);
        setError(null);
        try {
            const res = await setOverride({ companyId, limit: limit ? parseInt(limit) : null });
            if (res?.data) {
                setSuccess(res.data.message);
                onUpdated();
            }
        } catch (err) {
            setError(err?.error || "Failed to set override");
        }
    };

    const handleReset = async () => {
        setConfirmAction(null);
        setSuccess(null);
        setError(null);
        try {
            const res = await resetUsage({ companyId });
            if (res?.data) {
                setSuccess(res.data.message);
                onUpdated();
            }
        } catch (err) {
            setError(err?.error || "Failed to reset usage");
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Admin Controls
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label>Monthly Limit Override</Label>
                    <div className="flex gap-2">
                        <Input
                            type="number"
                            min="0"
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            placeholder="Leave empty to use plan limit"
                        />
                        <Button
                            onClick={() => setConfirmAction("override")}
                            disabled={settingOverride}
                            variant={limit ? "default" : "outline"}
                        >
                            {settingOverride && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                            {limit ? "Set" : "Remove"}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Overrides the plan's monthly limit. Clear to revert to plan default.
                    </p>
                </div>
                <div className="pt-2 border-t">
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmAction("reset")}
                        disabled={resetting}
                    >
                        {resetting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reset Usage to Zero
                    </Button>
                </div>
                {error && <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="h-4 w-4" />{error}</div>}
                {success && <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle className="h-4 w-4" />{success}</div>}

                <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {confirmAction === "reset" ? "Reset Automation Usage" : "Set Override Limit"}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {confirmAction === "reset"
                                    ? `Reset all automation counters to zero for ${companyName}? This cannot be undone.`
                                    : limit
                                        ? `Set monthly limit override to ${parseInt(limit).toLocaleString()} for ${companyName}?`
                                        : `Remove the override limit for ${companyName}? They will revert to their plan's default limit.`}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={confirmAction === "reset" ? handleReset : handleSetOverride}>
                                Confirm
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>
    );
}
